/**
 * RecipeNotesPanel Component
 * Single note field for a recipe (e.g., "Make it spicier next time")
 * Notes are stored in recipes.notes column
 */

import { useState, useEffect } from 'react'
import { Button, Loading } from '@talktoast/ui'
import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'

interface RecipeNotesPanelProps {
  recipeId: string
  currentNote: string | null
  onSaveNote?: (note: string) => Promise<void>
  onClearNote?: () => Promise<void>
}

export default function RecipeNotesPanel({
  recipeId,
  currentNote,
  onSaveNote,
  onClearNote
}: RecipeNotesPanelProps) {
  const [noteText, setNoteText] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  // Initialize from current note
  useEffect(() => {
    if (currentNote) {
      setNoteText(currentNote)
      setIsEditing(false)
    } else {
      setNoteText('')
      setIsEditing(true)
    }
  }, [currentNote])

  const handleSave = async () => {
    if (!noteText.trim() || !onSaveNote) return

    setIsSaving(true)
    try {
      await onSaveNote(noteText.trim())
      setIsEditing(false)
    } catch (error) {
      console.error('[RecipeNotesPanel] Save failed:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClear = async () => {
    if (!onClearNote) return

    const confirmed = confirm('Clear this note?')
    if (!confirmed) return

    setIsSaving(true)
    try {
      await onClearNote()
      setNoteText('')
      setIsEditing(true)
    } catch (error) {
      console.error('[RecipeNotesPanel] Clear failed:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    setNoteText(currentNote || '')
    setIsEditing(false)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault()
      handleSave()
    }
  }

  return (
    <div className="p-4 space-y-3 border-t border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ICONS.actions.edit className={`${getIconSize('sm')} ${getIconColor('primary')}`} />
          <h4 className="font-semibold text-gray-900 text-sm">My Notes</h4>
        </div>
        {currentNote && !isEditing && (
          <button
            onClick={handleEdit}
            className="p-1.5 text-xs text-gray-500 hover:text-toast-gold transition-colors"
          >
            Edit
          </button>
        )}
      </div>

      {/* Note Display/Edit */}
      {!onSaveNote ? (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600 text-center">
            Recipe notes will be available soon!
          </p>
        </div>
      ) : isEditing ? (
        <div className="space-y-2">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Add a note (e.g., 'Make it spicier next time')"
            disabled={isSaving}
            rows={4}
            maxLength={2000}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-toast-gold focus:border-transparent resize-none"
          />
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Ctrl+Enter to save</span>
            <span>{noteText.length}/2000</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!noteText.trim() || isSaving}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loading variant="spinner" size="sm" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <ICONS.actions.check className={getIconSize('sm')} />
                  <span>Save Note</span>
                </>
              )}
            </Button>
            {currentNote && (
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
                className="flex items-center justify-center gap-2"
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      ) : currentNote ? (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg relative group">
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{currentNote}</p>
          <button
            onClick={handleClear}
            disabled={isSaving}
            className="absolute top-2 right-2 p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
            title="Clear note"
          >
            {isSaving ? (
              <Loading variant="spinner" size="sm" />
            ) : (
              <ICONS.actions.delete className={getIconSize('sm')} />
            )}
          </button>
        </div>
      ) : (
        <div className="text-center py-4 text-xs text-gray-400">
          <p>No notes yet. Add your first note above!</p>
        </div>
      )}
    </div>
  )
}
